import React, { Fragment, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";

import {
  Box,
  Divider,
  Grid,
  styled,
  Typography
} from '@mui/material';

import ButtonBlue from "../../../../shared/general/ButtonBlue";
import AnimationBackdrop from "../../../../shared/general/Backdrop";
import DialogConfirmWithDate from "./dialogConfirmWithDate";

import {
  getEmployeeEWFByidEmp,
  joinEmployeeEWF,
  leftEmployeeEWF
} from '../../../../../../actions/employee-welfare-fund';
import {
  openNotificationAlert
} from "../../../../../../actions/notificationAlert";

const StyledRoot = styled(Box)(({ leftfund }) => ({
  "& .Topic":{
    fontWeight: 600, 
    fontSize: 20
  },
  "& .disbledEdit":{
    color:"#bdbdbd"
  },
  "& .text": {
    color: leftfund === "true" ? "#bdbdbd" : "initial",
  },
}));

const StyledDivider = styled(Divider)({
  marginTop: 16,
  borderWidth: "0px 0px thin",
  borderColor: "#919eab52",
  borderStyle: "dashed",
  width: "100%",
});

const EmployeeWelfareFund = () => {
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();

  const { result: employeeProfile } = useSelector(
    (state) => state.employeeProfile
  );
  const { result: employeeWelfareFund } = useSelector(
    (state) => state.employeeWelfareFund
  );

  const [isLoading, setIsLoading] = useState(false);
  const [isJoined, setIsJoined] = useState(false);
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [dialogConfirm, setDialogConfirm] = useState({
    isOpen: false,
    text: null
  });

  useEffect(() => {
    if(employeeProfile.idEmp){
      dispatch(getEmployeeEWFByidEmp(employeeProfile.idEmp));
    }
  },[employeeProfile]);

  useEffect(() => {
    if (employeeWelfareFund) {
      setIsJoined(employeeWelfareFund.isJoinFund === 1 ? true : false);
    }else{
      setIsJoined(false);
    }
  }, [employeeWelfareFund]);

  const formatDate = (date, pattern) => {
    if (!date || !dayjs(date).isValid()) {
      return "-";
    }
    return dayjs(date).format(pattern);
  };

  const hasValue = (value) => value !== null && value !== undefined;

  const handleOpenDialog = () => {
    setSelectedDate(dayjs());
    setDialogConfirm({
      ...dialogConfirm,
      isOpen: true,
      text: isJoined
        ? t("confirmLeaveEWF")
        : t("confirmJoinEWF"),
      text1: (
        <span style={{ color: "#ffab00", fontWeight: "500" }}>
          {isJoined ? t("selectLastEffectiveDate") : t("selectFundJoinDate")}
        </span>
      )
    });
  };

  const onCloseDialogConfirm = () => {
    setDialogConfirm({
      ...dialogConfirm,
      isOpen: false
    });
  }

  const handleJoinFund = async () => {
    onCloseDialogConfirm();
    const res = await dispatch(joinEmployeeEWF({
      id: employeeProfile.idEmp,
      date: dayjs(selectedDate).format("YYYY-MM-DD")
    }));
    if (res && res.status === 200) {
      dispatch(
        openNotificationAlert({
          type: "success",
          message: t("joinFundSuccessful"),
        })
      );
      dispatch(getEmployeeEWFByidEmp(employeeProfile.idEmp));
    } else {
      dispatch(
        openNotificationAlert({
          type: "error",
          message:
            (res && res.response && res.response.data && res.response.data.message && res.response.data.message === "fundNotOpenForJoining") ?
              t(res.response.data.message) :
              t("ErrorOccurredContactAdmin"),
        })
      );
    }
    setIsLoading(false);
  };

  const handleLeaveFund = async () => {
    onCloseDialogConfirm();
    const res = await dispatch(leftEmployeeEWF({
      id: employeeProfile.idEmp,
      date: dayjs(selectedDate).format("YYYY-MM-DD")
    }));
    if (res && res.status === 200) {
      dispatch(
        openNotificationAlert({
          type: "success",
          message: t("leaveFundSuccessful"),
        })
      );
      dispatch(getEmployeeEWFByidEmp(employeeProfile.idEmp));
    } else {
      dispatch(
        openNotificationAlert({
          type: "error",
          message:
            (res && res.data && res.data.message) ||
            t("ErrorOccurredContactAdmin"),
        })
      );
    }
    setIsLoading(false);
  };

  const startOfMonth = dayjs().startOf("month");

  const getMinDate = () => {
    const listHistory =
      (employeeWelfareFund && employeeWelfareFund.listHistory) || [];
    
    const lastHistory = listHistory[listHistory.length - 1];
    const value = lastHistory
      ? isJoined
        ? lastHistory.startDate
        : lastHistory.endDate
      : null;

    if (!value || !dayjs(value).isValid()) {
      return startOfMonth;
    }

    const latestChange = dayjs(value).startOf("day");
    const minDate = latestChange.isAfter(startOfMonth, "day")
      ? latestChange
      : startOfMonth;

    if (!isJoined && minDate.isSame(latestChange, "day")) {
      return latestChange.add(1, "day");
    }

    return minDate;
  };

  const isInScheduledFundPeriod = () => {
    const listHistory =
      (employeeWelfareFund && employeeWelfareFund.listHistory) || [];

    const lastHistory = listHistory.length > 0 ? listHistory[listHistory.length - 1] : null;

    return Boolean(
      lastHistory &&
      lastHistory.endDate &&
      !dayjs(lastHistory.endDate).isBefore(dayjs(), "day")
    );
  };

  const minDate = getMinDate();
  const maxDate = startOfMonth.add(1, "year");
  const monthFormatByLang =
    i18n.resolvedLanguage === "th" ? "DD MMMM BBBB" : "DD MMMM YYYY";

  return (
    <StyledRoot leftfund={`${!isJoined}`}>
      <Grid container spacing={2}>
        <Grid
          item
          xs={12}
          container
          justifyContent="space-between"
          alignItems="center"
          spacing={1}
        >
          <Grid item xs={12} sm sx={{ textAlign: { xs: "center", sm: "left" } }}>
            <Typography className="Topic">
              {t("joinEmployeeWelfareFund")}
            </Typography>
          </Grid>
          <Grid item xs={12} sm="auto" sx={{ textAlign: { xs: "center", sm: "right" } }}>
            {(employeeWelfareFund && !isInScheduledFundPeriod()) && (
              <>
                {(employeeWelfareFund.isJoinFund) ? (
                  <ButtonBlue variant="outlined" onClick={handleOpenDialog}>
                    {t("leaveFundEWF")}
                  </ButtonBlue>
                ) : (
                  <ButtonBlue variant="contained" onClick={handleOpenDialog}>
                    {t("joinFundEWF")}
                  </ButtonBlue>
                )}
              </>
            )}
          </Grid>
        </Grid>

        <StyledDivider />

        <Grid item xs={12}>
          <Typography className="Topic text">{t("AccumulatedRate")}</Typography>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography align="center" className="text" style={{ fontWeight: "600" }}>
            {`${t("EmployeeSection")}(%)`}
          </Typography>
          <Typography variant="h6" align="center" className="text">
            {employeeWelfareFund && hasValue(employeeWelfareFund.userEWF)
              ? employeeWelfareFund.userEWF 
              : "-"}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography align="center" className="text" style={{ fontWeight: "600" }}>
            {`${t("ContributionCompany")}(%)`}
          </Typography>
          <Typography variant="h6" align="center" className="text">
            {employeeWelfareFund && hasValue(employeeWelfareFund.vendorEWF)
              ? employeeWelfareFund.vendorEWF
              : "-"}
          </Typography>
        </Grid>
      </Grid>

      {employeeWelfareFund && (
        <Box style={{ marginTop: 24 }}>
          <Typography className="Topic">{t("joinFundHistoryEWF")}</Typography>
          {employeeWelfareFund.listHistory.length > 0 ? (
            employeeWelfareFund.listHistory.map((fundDate, index) => (
              <Fragment key={`${fundDate.startDate}_${index + 1}`}>
                <div style={{ display: "flex", marginTop: "16px" }}>
                  <div style={{ minWidth: "80px" }}>
                    <Typography style={{ fontWeight: "600" }}>
                      {`${t("RoundNo")} ${index + 1} :`}
                    </Typography>
                  </div>
                  <div>
                    <Typography>
                      {`${formatDate(fundDate.startDate, monthFormatByLang)}${
                        fundDate.endDate
                          ? ` - ${formatDate(fundDate.endDate, monthFormatByLang)}`
                          : ` - ${t("Current")}`
                      }`}
                    </Typography>
                  </div>
                </div>
              </Fragment>
            ))
          ) : (
            <Typography align="center" style={{ marginTop: "16px" }}>
              {t("noFundHistory")}
            </Typography>
          )}
        </Box>
      )}

      <DialogConfirmWithDate
        open={dialogConfirm.isOpen}
        handleClose={onCloseDialogConfirm}
        text={dialogConfirm.text}
        text1={dialogConfirm.text1}
        date={selectedDate}
        minDate={minDate}
        maxDate={maxDate}
        onChangeDate={(newValue) => setSelectedDate(newValue)}
        handleUpdateAdmin={() => {
          setIsLoading(true);
          if(isJoined){
            handleLeaveFund();
          }else{
            handleJoinFund();
          }
        }}
      />
      <AnimationBackdrop open={isLoading}/>
    </StyledRoot>
  );
};

export default EmployeeWelfareFund;